import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LineBathIcon from 'LuxKit/Icons/line/LineBathIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import GrowthInclusionList from './GrowthInclusionList'

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
}

/**
 * Reuses the data sources and formatters from ../VillaCardInclusions.tsx, but adapts
 * its two-column amenities grid to the Growth tile's single-column, three-row preview.
 * Capacity, bedrooms and bathrooms currently precede amenities. This is a provisional
 * application of the shared tile rules: villa-specific priority and the amenities
 * modal interaction still need product confirmation because the examples omit villas.
 */
function GrowthVillaInclusions({ offer }: Props) {
  const intl = useIntl()
  const amenities = offer.lowestPricePackage?.roomType.amenities ?? EmptyArray
  const numberOfBedrooms = offer.property.bedrooms
  const numberOfBathrooms = offer.property.bathrooms
  const capacity = useMemo(() => getMaxGuestCount(offer.lowestPricePackage?.roomRate?.capacities), [offer.lowestPricePackage?.roomRate?.capacities])

  return <GrowthInclusionList>
    {!!capacity && <BodyTextBlock variant="small" lineClamp={1} colour="light-foreground-alt" startIcon={<LineUsersAltIcon size="XS" />}>
      {intl.formatMessage(
        {
          id: '0w2PfdE_-rGvbwotjhU5',
          defaultMessage: '{count, plural, one {# guest} other {# guests}}',
          description: 'Guest capacity count on villa search card in the search interface',
        },
        { count: capacity },
      )}
    </BodyTextBlock>}
    {!!numberOfBedrooms && <BodyTextBlock variant="small" lineClamp={1} colour="light-foreground-alt" startIcon={<LineBedIcon size="XS" />}>
      {intl.formatMessage(
        {
          id: 'OzjFoGgYRzkMYjmUlmgP',
          defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
          description: 'Bedroom count on villa search card on the hotel or room booking page',
        },
        { count: numberOfBedrooms },
      )}
    </BodyTextBlock>}
    {!!numberOfBathrooms && <BodyTextBlock variant="small" lineClamp={1} colour="light-foreground-alt" startIcon={<LineBathIcon size="XS" />}>
      {intl.formatMessage(
        {
          id: 'cIF-oQUPkYGHxeub3f6s',
          defaultMessage: '{count, plural, one {# bathroom} other {# bathrooms}}',
          description: 'Bathroom count on villa search card on the hotel or room booking page',
        },
        { count: numberOfBathrooms },
      )}
    </BodyTextBlock>}
    {amenities.map(amenity => <OfferInclusionItem key={amenity.id} inclusion={amenity} size="small" lineClamp={1} />)}
  </GrowthInclusionList>
}

export default GrowthVillaInclusions
